import { PLP_CAROUSEL_IMAGE_DURATION_MS } from '../../constants';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { ProductCardMedia } from '../../domain/models/ProductListing';

function CarouselImage({ item, alt, shouldLoad }: { item: ProductCardMedia; alt: string; shouldLoad: boolean }) {
  const [loaded, setLoaded] = useState(false);
  if (!shouldLoad && !loaded) return <span className="block h-full w-full bg-[#F0F0F0]" aria-hidden="true" />;
  return (
    <img src={item.type === 'image' ? item.url : item.poster}
      srcSet={item.type === 'image' ? `${item.url} 414w, ${item.url2x} 828w` : undefined}
      sizes="(max-width: 969px) 50vw, 33vw"
      alt={alt} onLoad={() => setLoaded(true)}
      loading="lazy" decoding="async" draggable={false}
      className="h-full w-full select-none object-cover" />
  );
}


interface ProductCardMediaCarouselProps {
  designId: number;
  designName: string;
  media: ProductCardMedia[];
  isActive: boolean;
  autoSlide: boolean;
  showProgress?: boolean;
  onProductClick: () => void;
}

function ProductCardMediaCarouselComponent({
  designId, designName, media, isActive, autoSlide, showProgress = false, onProductClick,
}: ProductCardMediaCarouselProps) {
  const { analytics } = useAnalytics();
  const manualInteraction = useRef(false);
  const [viewportRef, api] = useEmblaCarousel({ loop: true, align: 'start' });
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [manualDesignId, setManualDesignId] = useState<number | null>(null);
  const manuallySelected = manualDesignId === designId;
  const videoRef = useRef<HTMLVideoElement>(null);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const suppressClick = useRef(false);
  const goNext = useCallback(() => { manualInteraction.current = false; api?.scrollNext(); }, [api]);

  useEffect(() => {
    if (!api) return;
    const select = () => {
      const index = api.selectedScrollSnap();
      setActiveIndex(index);
      if (!manualInteraction.current) return;
      const attributes = {
        'Product Id': String(designId),
        'Media Type': media[index]?.type === 'video' ? 'Video' : 'Image',
        Position: index + 1,
        'Media Index': index,
      };
      analytics?.trackEvent('Product Media Viewed', attributes);
      analytics?.trackEvent('Product Media Thumbnail Scrolled', attributes);
    };
    const reInit = () => { manualInteraction.current = false; setActiveIndex(api.selectedScrollSnap()); };
    const settle = () => { manualInteraction.current = false; };
    const pointerDown = () => { manualInteraction.current = true; setDragging(true); setManualDesignId(designId); };
    const pointerUp = () => setDragging(false);
    api.on('select', select).on('reInit', reInit).on('settle', settle).on('pointerDown', pointerDown).on('pointerUp', pointerUp);
    return () => {
      api.off('select', select).off('reInit', reInit).off('settle', settle).off('pointerDown', pointerDown).off('pointerUp', pointerUp);
    };
  }, [api, designId, analytics, media]);

  useEffect(() => {
    manualInteraction.current = false;
    api?.scrollTo(0, true);
  }, [api, designId]);

  useEffect(() => {
    manualInteraction.current = false;
    if (autoSlide) api?.scrollTo(0, true);
  }, [api, autoSlide]);

  useEffect(() => {
    if (!autoSlide || !isActive || dragging || manuallySelected || media.length < 2 || media[activeIndex]?.type === 'video') return;
    const timer = window.setTimeout(goNext, PLP_CAROUSEL_IMAGE_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [activeIndex, autoSlide, dragging, goNext, isActive, manuallySelected, media]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isActive && !dragging) video.play().catch(() => {});
    else video.pause();
  }, [activeIndex, dragging, isActive]);

  return (
    <div className="relative h-full w-full">
      <div ref={viewportRef} className="h-full overflow-hidden"
        onPointerDown={event => {
          pointerStart.current = { x: event.clientX, y: event.clientY };
          suppressClick.current = false;
        }}
        onPointerMove={event => {
          const start = pointerStart.current;
          if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) suppressClick.current = true;
        }}
        onPointerUp={() => { pointerStart.current = null; }}
        onPointerCancel={() => { pointerStart.current = null; suppressClick.current = true; }}
      >
        <div className="flex h-full touch-pan-y">
          {media.map((item, index) => (
            <button key={`${designId}-${index}-${item.url}`} type="button"
              className="relative block h-full min-w-0 flex-[0_0_100%] overflow-hidden"
              aria-label={designName} tabIndex={index === activeIndex ? 0 : -1}
              onClick={event => {
                if (suppressClick.current || event.defaultPrevented) { event.preventDefault(); return; }
                onProductClick();
              }}
              onKeyDown={event => {
                if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                  event.preventDefault();
                  manualInteraction.current = true;
                  setManualDesignId(designId);
                  if (event.key === 'ArrowRight') api?.scrollNext();
                  else api?.scrollPrev();
                }
              }}
            >
              {item.type === 'video' && index === activeIndex ? (
                <video ref={videoRef} key={item.url} src={item.url} poster={item.poster}
                  muted playsInline preload="none"
                  onEnded={autoSlide && media.length > 1 ? goNext : undefined}
                  className="h-full w-full object-cover" />
              ) : (
                <CarouselImage item={item}
                  shouldLoad={index === activeIndex || (isActive && index === (activeIndex + 1) % media.length)}
                  alt={`${designName} - ${item.type === 'image' ? 'image' : 'video'} ${index + 1}`}
                />
              )}
            </button>
          ))}
        </div>
      </div>
      {showProgress && media.length > 1 && (
        <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-1 overflow-hidden rounded-full bg-[#AAA]">
          <span className="block h-full rounded-full bg-[#103064] transition-transform duration-300"
            style={{ width: `${100 / media.length}%`, transform: `translateX(${activeIndex * 100}%)` }} />
        </span>
      )}
    </div>
  );
}

export const ProductCardMediaCarousel = memo(ProductCardMediaCarouselComponent);
